import { useState } from "react";
import { Download } from "lucide-react";
import { PageHeader } from "../../../components/PortfolioPerformance/page-header";
import { Panel } from "../../../components/PortfolioPerformance/panel";
import { Skeleton } from "../../../ui/skeleton";
import { Button } from "../../../ui/button";
import { ExposureRangesSection } from "../../../components/PortfolioPerformance/exposure-ranges";
import { TrendLineChart } from "../../../components/PortfolioPerformance/charts/trend-line-chart";
import {
  downloadExposureRange,
  useExposureTrackingDetail,
  useExposureTrackingStats,
  useExposureTrackingTrend,
} from "../lib/api/exposure-tracking";
import { cohortColor } from "../lib/api/lifetime-loss-projections";
import { cn } from "../../../utils/common/cn";

/** "$250K–$750K Exposure (% Owned)" -> "exposure_250K-750K.csv", ">$1M Exposure (% Owned)" -> "exposure_over_1M.csv", "Franchise >$1M
 *  Exposure (% Owned)" -> "exposure_Franchise_over_1M.csv": the file is named by the range (its segment, if it has one, and its limits),
 *  in characters a file name can hold. */
const rangeFileName = (label: string) =>
  `exposure_${label
    .replace(/ Exposure \(% Owned\)$/, "")
    .replace(/\$/g, "")
    .replace("–", "-")
    .replace(">", "over_")
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9_.-]/g, "")}.csv`;

/** The card's Download CSV: the customers in that range at the latest file date, streamed by the backend. */
function RangeDownloadButton({ rangeNo, label }: { rangeNo: number; label: string }) {
  const [downloading, setDownloading] = useState(false);
  const [failed, setFailed] = useState(false);

  async function handleDownload() {
    setDownloading(true);
    setFailed(false);
    try {
      await downloadExposureRange(rangeNo, rangeFileName(label));
    } catch {
      setFailed(true);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="mt-3 flex items-center gap-2">
      <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 px-2 text-xs" disabled={downloading} onClick={handleDownload}>
        <Download className="size-3.5" />
        {downloading ? "Downloading…" : "Download CSV"}
      </Button>
      {failed ? <span className="text-xs text-destructive">Download failed</span> : null}
    </div>
  );
}

function DetailTable({
  rows,
  extraLabel,
}: {
  rows: { month: string; ownedNI: string; pctOwned: string; extra?: string }[];
  extraLabel: string;
}) {
  // Compact: every range's card shares one row, so each is narrow. A card that is narrower than its table scrolls sideways.
  return (
    <div className="overflow-x-auto">
      <table className="w-full whitespace-nowrap text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="pb-1.5 pr-2">Month</th>
            <th className="pb-1.5 pr-2 text-right">Owned NI</th>
            <th className="pb-1.5 text-right">% Owned</th>
            <th className="pb-1.5 pl-3 text-left">{extraLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.month} className="border-t border-border">
              <td className="py-1.5 pr-2">{r.month}</td>
              <td className="py-1.5 pr-2 text-right tabular-nums">{r.ownedNI}</td>
              <td className="py-1.5 text-right tabular-nums">{r.pctOwned}</td>
              <td className="py-1.5 pl-3 text-muted-foreground">{r.extra ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CardError({ error }: { error: unknown }) {
  return (
    <div
      role="alert"
      className="border border-decline/50 bg-decline/10 px-4 py-3 text-sm text-foreground"
    >
      <p className="font-semibold">Could not load this.</p>
      <p className="mt-1 text-muted-foreground">
        {error instanceof Error ? error.message : "Unknown failure."}
      </p>
    </div>
  );
}

/** The Up to $250K line takes the palette's first colour; a range's line (an office's too) follows the range's number (its position in
 *  the list of ranges), from the colours after it, so no two lines share one. */
const FIXED_SERIES_COLOR: Record<string, string> = {
  upTo250k: cohortColor(0),
};

function seriesColor(key: string): string {
  return FIXED_SERIES_COLOR[key] ?? cohortColor(Number(key.replace("range", "")) + 1);
}

export default function ExposureTracking() {
  // 3 independent requests (see exposure-tracking.ts's own comment on why) -- react-query
  // fires all of them concurrently, so this page's perceived load time is bounded by the
  // single slowest card, not their sum.
  const statsQ = useExposureTrackingStats();
  const trendQ = useExposureTrackingTrend();
  const detailQ = useExposureTrackingDetail();

  return (
    <div>
      <PageHeader
        title="Exposure Tracking"
        subtitle="Large-exposure concentration by customer exposure range, tracked monthly."
        asOfPage="exposure-tracking"
      />

      {/* Each range that has "show card" on, in the order a contributor has set on the approved ranges below (drag a range by its handle).
          Each card spans five grid rows (label / value / amount / change / button) and hands them to its own five lines (CSS subgrid), so
          when one label wraps onto a second line, the others keep that space too and every card's value, amount, change and Download
          button sit on the same line across the row. The space between rows of cards is each card's bottom margin, not a row gap, which
          subgrid would also put between a card's own lines (the container's -mb-4 takes back the last one). */}
      <div className="-mb-4 mt-8 grid grid-cols-1 gap-x-4 sm:grid-cols-2 lg:grid-cols-5">
        {statsQ.isError ? (
          <div className="mb-4 sm:col-span-2 lg:col-span-5">
            <CardError error={statsQ.error} />
          </div>
        ) : statsQ.isPending || !statsQ.data ? (
          Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="mb-4 h-24 w-full" />)
        ) : (
          statsQ.data.stats.map((s) => (
            <div
              key={s.label}
              className="mb-4 row-span-5 grid gap-y-0 border border-border bg-card p-5 [grid-template-rows:subgrid]"
            >
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {s.label}
              </p>
              <p className="mt-1 self-start text-2xl font-semibold tabular-nums text-foreground">
                {s.value}
              </p>
              <p className="mt-1 self-start text-xs text-muted-foreground">{s.sub}</p>
              {/* The change against the previous month, as the Growth & Exposure concentration cards show it: "▲ 0.4pt vs Sep'26". */}
              <p
                className={cn(
                  "mt-3 self-start text-xs font-medium tabular-nums",
                  s.trend.direction === "flat" ? "text-muted-foreground" : s.trend.direction === "up" ? "text-approve" : "text-decline",
                )}
              >
                {s.trend.direction === "up" ? "▲" : s.trend.direction === "down" ? "▼" : "■"} {s.trend.value}
                {s.trend.comparison ? <span className="ml-1 font-normal text-muted-foreground">{s.trend.comparison}</span> : null}
              </p>
              {/* always a fifth line, so a card with no download keeps the row the others' buttons sit on */}
              {s.rangeNo ? <RangeDownloadButton rangeNo={s.rangeNo} label={s.label} /> : <div />}
            </div>
          ))
        )}
      </div>

      <Panel
        className="mt-6"
        title="Customer Exposure Trend"
        description="Owned NI at each quarter end by customer exposure size. A range scoped to an office counts only that office's NI, so it overlaps the size lines."
      >
        {trendQ.isError ? (
          <CardError error={trendQ.error} />
        ) : trendQ.isPending || !trendQ.data ? (
          <Skeleton className="h-60 w-full" />
        ) : (
          <TrendLineChart
            data={trendQ.data.trend}
            series={trendQ.data.series.map((s) => ({
              dataKey: s.key,
              name: s.name,
              color: seriesColor(s.key),
            }))}
            currency
            height={300}
          />
        )}
      </Panel>

      {/* The tables under the chart: each range with "show trend" on, in the same order. From lg up they all share ONE row, however many
          there are (one equal column each), so the cards shrink as ranges are added instead of wrapping onto a second row. */}
      <div className="mt-6 grid grid-cols-1 gap-3 lg:auto-cols-fr lg:grid-flow-col">
        {detailQ.isError ? (
          <div>
            <CardError error={detailQ.error} />
          </div>
        ) : detailQ.isPending || !detailQ.data ? (
          // No titles while loading: they carry the approved threshold and ranges, which are not known yet.
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-52 w-full" />)
        ) : (
          <>
            {detailQ.data.cards.map((card) => (
              <Panel
                key={card.key}
                title={`${card.label} Detail`}
                className="min-w-0 p-3 [&_h2]:text-xs [&_h2]:leading-snug [&>div:last-child]:mt-3"
              >
                <DetailTable rows={card.detail} extraLabel="Customers" />
              </Panel>
            ))}
          </>
        )}
      </div>

      <ExposureRangesSection />
    </div>
  );
}
